import { useAuth } from "../lib/AuthContext";
import { isMobile } from "react-device-detect";
import {
  Users,
  MailCheck,
  SettingsIcon,
  LogOut,
  FileText,
  Calendar,
  Clock,
  Images,
  CalendarCogIcon,
  Share2Icon,
  Shield,
  Boxes,
} from "lucide-react";
import {
  fetchReorderSupplies,
  fetchExpiringSupplies,
  fetchInventoryOwners,
} from "../apis/api.inventory";
import { todayISODate } from "../utils/inventoryExpiration";
import React, { useEffect, useState } from "react";
import Spinner from "./common/Spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./common/Tooltip";
import config from "../config";
import useBrandTranslation from "../hooks/useBrandTranslation";
import { apiJsonHeaders } from "../utils/apiLocale";
import useDoctorMode, { DOCTOR_MODE } from "../hooks/useDoctorMode";
import DoctorModeSwitch from "./common/DoctorModeSwitch";
import ActingPatientSelector from "./ActingPatientSelector";

const NOTIFICACIONES_COMING_SOON = (t) =>
  t(
    "home.comingSoonNotifications",
    "Próximamente podrás enviarle a todos tus pacientes mensajes por Whatsapp o Email con un solo click!"
  );

const HISTORIAS_CLINICAS_COMING_SOON = (t) =>
  t("home.comingSoonClinicalHistory", "Próximamente. Estamos mejorando la experiencia.");

function NavCard({
  item,
  index,
  showBadge,
  badgeLabel,
  onDisabledClick,
}) {
  const isDisabled = Boolean(item.disabled);
  const disabledMessage = item.disabledMessage ?? "";

  const card = (
    <div
      className={`${isDisabled ? "" : "group"} flex items-center justify-center p-4 rounded-lg border-t-4 border-[${config.theme.colors.secondaryColor}] h-32 shadow-lg transition-colors duration-200 w-40 bg-white ${
        isDisabled
          ? "opacity-60 cursor-not-allowed"
          : `cursor-pointer hover:bg-[${config.theme.colors.primaryColor}] active:bg-[${config.theme.colors.primaryColor}] hover:border-[${config.theme.colors.primaryColor}] active:border-[${config.theme.colors.primaryColor}]`
      }`}
      role="button"
      aria-disabled={isDisabled}
      aria-describedby={isDisabled ? `coming-soon-${index}` : undefined}
      tabIndex={0}
      onClick={() => {
        if (isDisabled) {
          onDisabledClick(disabledMessage);
          return;
        }
        window.location.href = item.path;
      }}
      onKeyDown={(e) => {
        if (isDisabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          window.location.href = item.path;
        }
      }}
    >
      <div className="flex flex-col items-center gap-2">
        <div className="relative inline-block">
          <item.icon className={`w-8 h-8 sm:w-16 sm:h-16 text-gray-600 transition-colors duration-200${isDisabled ? "" : " group-hover:text-white group-active:text-white"}`} />
          {showBadge && (
            <span
              className="absolute -top-1.5 -right-1.5 h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-red-500 border-2 border-white"
              aria-label={badgeLabel}
            />
          )}
        </div>
        <span className={`text-sm text-center text-gray-900 transition-colors duration-200${isDisabled ? "" : " group-hover:text-white group-active:text-white"}`}>
          {item.label}
        </span>
      </div>
    </div>
  );

  if (isDisabled && !isMobile) {
    return (
      <Tooltip key={`${item.path}-${item.label}-${index}`}>
        <TooltipTrigger asChild>{card}</TooltipTrigger>
        <TooltipContent
          id={`coming-soon-${index}`}
          side="top"
          sideOffset={8}
          intent="dark"
          className="max-w-[min(90vw,22rem)] text-center leading-snug"
        >
          {disabledMessage}
        </TooltipContent>
      </Tooltip>
    );
  }

  return card;
}

export default function Home() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const [doctorMode] = useDoctorMode();
  const [unreadAssociationCount, setUnreadAssociationCount] = useState(0);
  const [reorderCount, setReorderCount] = useState(0);
  const [expiringCount, setExpiringCount] = useState(0);
  const [mobileComingSoonMessage, setMobileComingSoonMessage] = useState(null);
  const isProfessionalPendingValidation =
    user?.role === "doctor" && user?.is_professional_enabled === false;

  useEffect(() => {
    if (!user || user.role !== "doctor") return;
    const loadUnreadAssociationMessages = async () => {
      try {
        const response = await fetch(
          `${config.baseUrlApi}/professionals/${user.id}/messages`,
          { headers: apiJsonHeaders() }
        );
        if (!response.ok) return;
        const result = await response.json();
        const messages = result.data || [];
        const count = messages.filter((msg) => !msg.read_at).length;
        setUnreadAssociationCount(count);
      } catch (err) {
        console.error("Error loading association messages count:", err);
      }
    };
    loadUnreadAssociationMessages();
  }, [user]);

  // Insumos a reponer o por vencer -> punto rojo en la card de Inventario (profesional/institución/secretaria).
  useEffect(() => {
    if (!user || !["doctor", "institution", "secretary"].includes(user.role)) return;
    let cancelled = false;
    // La secretaria suma los avisos de los inventarios de todos sus profesionales.
    const countAll = async (ownerIds, fetcher) => {
      const lists = await Promise.all(ownerIds.map((id) => fetcher(id).catch(() => [])));
      return lists.reduce((acc, list) => acc + (Array.isArray(list) ? list.length : 0), 0);
    };
    const loadCounts = async () => {
      try {
        const ownerIds =
          user.role === "secretary"
            ? (await fetchInventoryOwners(user.id)).map((o) => o.id)
            : [user.id];
        const today = todayISODate();
        const [reorder, expiring] = await Promise.all([
          countAll(ownerIds, fetchReorderSupplies),
          countAll(ownerIds, (id) => fetchExpiringSupplies(id, today)),
        ]);
        if (!cancelled) {
          setReorderCount(reorder);
          setExpiringCount(expiring);
        }
      } catch (err) {
        if (!cancelled) {
          setReorderCount(0);
          setExpiringCount(0);
        }
      }
    };
    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const getNavItems = () => {
    if (!user) return [];

    const settingsItem = {
      label: t("common.settings", "Settings"),
      icon: SettingsIcon,
      path: "/profile",
    };
    const logoutItem = {
      label: t("common.logout", "LogOut"),
      icon: LogOut,
      path: "/logout",
    };

    if (isProfessionalPendingValidation) {
      return [settingsItem, logoutItem];
    }

    const navItems = [];

    switch (user.role) {
      case "doctor":
        // El profesional puede operar como paciente (atenderse con otro profesional).
        // El modo se elige con DoctorModeSwitch (arriba a la derecha / header del Home).
        if (doctorMode === DOCTOR_MODE.PATIENT) {
          navItems.push(
            {
              label: t("home.nav.bookAppointment", "Reservar Turno"),
              icon: Calendar,
              path: "/patient/search",
            },
            {
              label: t("home.nav.myAppointments", "Mis Turnos"),
              icon: Calendar,
              path: "/patient/appointments",
            },
            settingsItem,
            logoutItem
          );
          break;
        }
        navItems.push(
          {
            label: t("home.nav.configureSchedule", "Configurar Horarios"),
            icon: CalendarCogIcon,
            path: "/doctor/schedule",
          },
          {
            label: t("home.nav.viewAppointments", "Ver Turnos"),
            icon: Calendar,
            path: "/doctor/appointments",
          },
          {
            label: t("home.nav.assignAppointments", "Asignar Turnos"),
            icon: Clock,
            path: "/secretary/appointments",
          },
          {
            label: t("home.nav.shareSchedules", "Compartir Horarios"),
            icon: Share2Icon,
            path: "/doctor/share",
          },
          {
            label: t("home.nav.clinicalHistory", "Historias Clínicas"),
            icon: FileText,
            path: "/clinical-history/manage",
          },
          {
            label: t("home.nav.inventory", "Inventario"),
            icon: Boxes,
            path: "/inventory",
          },
          {
            label: t("home.nav.waitingRoom", "Recepción de Pacientes"),
            icon: Users,
            path: "/waiting-room-doctor",
          },
          {
            label: t("home.nav.notifications", "Notificaciones"),
            icon: MailCheck,
            path: "/messaging",
            disabled: true,
            disabledMessage: t(
              "home.comingSoonNotifications",
              NOTIFICACIONES_COMING_SOON(t)
            ),
          },
          {
            label: t("home.nav.informedConsent", "Consentimiento Informado"),
            icon: FileText,
            path: "/informed-consent",
          },
          settingsItem,
          logoutItem
        );
        break;

      case "secretary":
        navItems.push(
          {
            label: t("home.nav.sharedSchedules", "Turnos y Horarios Compartidos"),
            icon: Calendar,
            path: "/secretary/doctor-selection",
          },
          {
            label: t("home.nav.waitingRoom", "Recepción de Pacientes"),
            icon: Users,
            path: "/waiting-room-secretary",
          },
          {
            label: t("home.nav.inventory", "Inventario"),
            icon: Boxes,
            path: "/inventory",
          },
          {
            label: t("home.nav.notifications", "Notificaciones"),
            icon: MailCheck,
            path: "/messaging",
            disabled: true,
            disabledMessage: t(
              "home.comingSoonNotifications",
              NOTIFICACIONES_COMING_SOON(t)
            ),
          },
          {
            label: t("home.nav.informedConsent", "Consentimiento Informado"),
            icon: FileText,
            path: "/informed-consent",
          },
          settingsItem,
          logoutItem
        );
        break;

      case "institution":
        navItems.push(
          {
            label: t("home.nav.institutionSchedules", "Horarios Compartidos"),
            icon: Calendar,
            path: "/secretary/schedule",
          },
          {
            label: t("home.nav.assignAppointments", "Asignar Turnos"),
            icon: Clock,
            path: "/secretary/doctor-selection",
          },
          {
            label: t("home.nav.clinicalHistory", "Historias Clínicas"),
            icon: FileText,
            path: "/clinical-history/manage",
            disabled: true,
            disabledMessage: t(
              "home.comingSoonClinicalHistory",
              HISTORIAS_CLINICAS_COMING_SOON(t)
            ),
          },
          {
            label: t("home.nav.inventory", "Inventario"),
            icon: Boxes,
            path: "/inventory",
          },
          {
            label: t("home.nav.informedConsent", "Consentimiento Informado"),
            icon: FileText,
            path: "/informed-consent",
          },
          settingsItem,
          logoutItem
        );
        break;

      case "association":
        navItems.push(
          {
            label: t("home.nav.messaging", "Mensajería"),
            icon: MailCheck,
            path: "/association/messaging",
          },
          settingsItem,
          logoutItem
        );
        break;

      case "admin":
        navItems.push(
          {
            label: t("home.nav.administration", "Administración"),
            icon: Shield,
            path: "/admin",
          },
          settingsItem,
          logoutItem
        );
        break;

      case "patient":
        navItems.push(
          {
            label: t("home.nav.bookAppointment", "Reservar Turno"),
            icon: Calendar,
            path: "/patient/search",
          },
          {
            label: t("home.nav.myAppointments", "Mis Turnos"),
            icon: Clock,
            path: "/patient/appointments",
          },
          {
            label: t("home.nav.myStudies", "Mis Estudios"),
            icon: Images,
            path: "/profile?tab=studies",
          },
          settingsItem,
          logoutItem
        );
        break;

      default:
        navItems.push(settingsItem, logoutItem);
    }

    return navItems;
  };

  const navItems = getNavItems();

  const handleDisabledClick = (message) => {
    if (isMobile && message) {
      setMobileComingSoonMessage(message);
    }
  };

  if (!user) return <Spinner className="w-10 h-10 mx-auto" />;

  return (
    <div className="w-full min-h-full bg-gray-50 flex flex-col items-center">
      <div className={`w-full max-w-[22rem] px-4 py-8 flex flex-col items-center ${isMobile ? "pb-24" : "pb-10"}`}>
        <header className="text-center mb-6 w-full">
          <p className="inline-block font-extrabold text-gray-900 tracking-tight">
            {t("home.welcomeUser", "Bienvenid@ {{name}} ({{role}})", {
              name: user?.name,
              role: user?.role,
            })}
          </p>
          <p className="text-gray-900 italic">
            {t("home.selectOption", "selecciona la opcion deseada")}
          </p>
          {user?.role === "doctor" && !isProfessionalPendingValidation && (
            <div className="mt-4 w-full">
              <p className="text-xs font-medium text-gray-500 mb-1 text-left">
                {t("home.mode.operateAs", "Operar como")}
              </p>
              <DoctorModeSwitch />
            </div>
          )}
          {(user?.role === "patient" ||
            (user?.role === "doctor" &&
              doctorMode === DOCTOR_MODE.PATIENT &&
              !isProfessionalPendingValidation)) && (
            <div className="mt-3 w-full">
              <ActingPatientSelector />
            </div>
          )}
          {isProfessionalPendingValidation && (
            <div className="mt-4 w-full rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 text-sm text-left">
              {t("home.pendingValidation", "Tu cuenta profesional está pendiente de validación...")}
            </div>
          )}
        </header>

        <div className="grid grid-cols-2 gap-4 justify-items-center">
          {navItems.map((item, index) => (
            <NavCard
              key={`${item.path}-${item.label}-${index}`}
              item={item}
              index={index}
              showBadge={
                (item.path === "/association/search" && unreadAssociationCount > 0) ||
                (item.path === "/inventory" && (reorderCount > 0 || expiringCount > 0))
              }
              badgeLabel={
                item.path === "/inventory"
                  ? reorderCount > 0 && expiringCount > 0
                    ? t("home.inventoryReorderAndExpiring", "Tenés insumos por reponer y por vencer")
                    : expiringCount > 0
                    ? t("home.inventoryExpiring", "Tenés insumos por vencer")
                    : t("home.inventoryReorder", "Tenés insumos por reponer")
                  : t("home.unreadMessages", "{{count}} mensajes sin leer", {
                      count: unreadAssociationCount,
                    })
              }
              onDisabledClick={handleDisabledClick}
            />
          ))}
        </div>
      </div>

      {mobileComingSoonMessage && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default bg-black/40"
            aria-label={t("home.closeMessage", "Cerrar mensaje")}
            onClick={() => setMobileComingSoonMessage(null)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="coming-soon-mobile-title"
            className="fixed inset-x-4 top-20 z-50 max-h-[50vh] overflow-y-auto rounded-xl border border-gray-700 bg-gray-900 p-4 text-white shadow-xl"
          >
            <p
              id="coming-soon-mobile-title"
              className="text-sm leading-relaxed"
            >
              {mobileComingSoonMessage}
            </p>
            <button
              type="button"
              className="mt-4 w-full rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-900"
              onClick={() => setMobileComingSoonMessage(null)}
            >
              {t("common.understood", "Entendido")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
